// Nakládka materiálu — první úkol směny.
//
// Přestavba 2026-09-22: materiál už není přiřazený k trase v noci. Mobilka se
// zeptá poolu „stojím na svém depu a mám tyhle zastávky, co tu pro ně máš"
// a NÁROK VZNIKÁ AŽ POTVRZENÍM NAKLÁDKY. Dotaz jen čte, takže se na tentýž
// sáček můžou dívat dva řidiči a nic se nerozbije — prohraje ten, kdo potvrdí
// později, a dozví se proč.
//
// Sken i ruční odškrtnutí končí stejně, seznamem pečetí. Obojí je dostupné
// zároveň schválně: když čtečka zlobí, řidič to naťuká a jede dál.

import { useEffect, useMemo, useRef, useState } from "react";
import { useHistory } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { IonContent, IonPage, IonSpinner } from "@ionic/react";
import { normalizeSeal } from "@routera/shared";
import { materialApi, type MaterialSacek } from "../lib/api";
import { useMaterialStore } from "../store/materialStore";
import { ThemeToggle } from "../components/ThemeToggle";

export function MaterialScreen() {
  const history = useHistory();
  const qc = useQueryClient();
  const { setTotal } = useMaterialStore();

  const { data, isLoading } = useQuery({
    queryKey: ["mobile", "material", "k-nalozeni"],
    queryFn: materialApi.kNalozeni,
    staleTime: 0,
    refetchOnWindowFocus: true,
  });

  const sacky: MaterialSacek[] = data?.sacky ?? [];
  const zprava = data?.zprava ?? null;

  // Odškrtané pečetě — sem padá sken i ruční klik, backend mezi nimi nerozlišuje.
  const [vzato, setVzato] = useState<Set<string>>(new Set());
  const [scan, setScan] = useState("");
  const [hlaska, setHlaska] = useState<string | null>(null);
  const [nepovedlo, setNepovedlo] = useState<Array<{ pecet: string; duvod: string }>>([]);
  const scanRef = useRef<HTMLInputElement>(null);

  useEffect(() => { if (!isLoading) setTotal(sacky.length); }, [isLoading, sacky.length, setTotal]);

  const podlePeceti = useMemo(() => {
    const m = new Map<string, MaterialSacek>();
    sacky.forEach((s) => m.set(s.pecet, s));
    return m;
  }, [sacky]);

  const prepni = (pecet: string) =>
    setVzato((prev) => {
      const next = new Set(prev);
      next.has(pecet) ? next.delete(pecet) : next.add(pecet);
      return next;
    });

  const nactiSken = () => {
    const kod = normalizeSeal(scan);
    setScan("");
    if (!kod) return;
    if (!podlePeceti.has(kod)) {
      // Nejčastější záměna: příjmová pečeť jednorázového obalu. Vypadá v ruce
      // podobně, ale patří do úplně jiné řady — ať to řidič nehledá pět minut.
      setHlaska(`Pečeť ${kod} tu není. Není to příjmová pečeť od bedny?`);
      return;
    }
    setHlaska(null);
    setVzato((prev) => new Set(prev).add(kod));
  };

  const potvrdit = useMutation({
    mutationFn: () => materialApi.nalozeno([...vzato]),
    onSuccess: (r) => {
      setNepovedlo(r.nepovedlo ?? []);
      setVzato(new Set());
      qc.invalidateQueries({ queryKey: ["mobile", "material", "k-nalozeni"] });
      qc.invalidateQueries({ queryKey: ["mobile", "material", "nesu"] });
      if (!r.nepovedlo?.length) history.goBack();
    },
  });

  const vse = () => setVzato(new Set(sacky.map((s) => s.pecet)));

  return (
    <IonPage>
      <IonContent scrollY={false}>
        <div className="ck scenebg" style={{ height: "100%" }}>
          <div className="top">
            <button className="back" onClick={() => history.goBack()}>‹</button>
            <div style={{ flex: 1, minWidth: 0 }}>
              <b style={{ fontSize: 19, display: "block" }}>Naložit materiál</b>
              <span style={{ display: "block", fontSize: 13.5, color: "var(--soft)" }}>
                {data?.depo ? `depo ${data.depo}` : "počítárna C.I.T"}
              </span>
            </div>
            <ThemeToggle />
          </div>

          <div className="stopbody"><div className="pad-x">
            {isLoading ? (
              <div style={{ textAlign: "center", padding: 40 }}><IonSpinner name="crescent" /></div>
            ) : sacky.length === 0 ? (
              <div style={{ padding: "26px 0", color: "var(--dim)", fontSize: 14, textAlign: "center" }}>
                {zprava ?? "Dnes nemáš žádný materiál."}
              </div>
            ) : (
              <>
                <div style={{ marginTop: 14, display: "flex", gap: 8 }}>
                  <input
                    ref={scanRef}
                    className="mono"
                    value={scan}
                    autoFocus
                    inputMode="text"
                    placeholder="Naskenuj pečeť"
                    onChange={(e) => setScan(e.target.value)}
                    onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); nactiSken(); } }}
                    style={{
                      flex: 1, padding: "12px 10px", fontSize: 16, borderRadius: 10,
                      border: "1px solid var(--line)", background: "var(--card)", color: "var(--ink)",
                    }}
                  />
                  <button className="matb" style={{ padding: "0 14px" }} onClick={nactiSken}>Přidat</button>
                </div>

                {hlaska && (
                  <div className="stop-why warn" style={{ marginTop: 10 }}>{hlaska}</div>
                )}

                <div className="cap" style={{ marginTop: 16, marginBottom: 0, display: "flex" }}>
                  K naložení
                  <span className="spacer" />
                  <button
                    onClick={vse}
                    style={{ background: "none", border: 0, color: "var(--soft)", fontSize: 12.5 }}
                  >
                    označit vše
                  </button>
                </div>

                {sacky.map((s) => {
                  const ok = vzato.has(s.pecet);
                  return (
                    <div
                      key={s.id}
                      className="card"
                      style={{ marginTop: 12, borderColor: ok ? "var(--ok)" : undefined }}
                      onClick={() => prepni(s.pecet)}
                    >
                      <div className="cap" style={{ display: "flex", marginBottom: 6 }}>
                        {s.cil_id} {s.cil_sheet.toUpperCase()}
                        <span className="spacer" />
                        {s.typ === "vymena" && <span style={{ color: "var(--soft)" }}>výměna</span>}
                        {ok && <span style={{ color: "var(--ok)", letterSpacing: 0, marginLeft: 8 }}>✓ mám</span>}
                      </div>
                      <div className="mono" style={{ fontSize: 16, fontWeight: 700, color: "var(--ink)", letterSpacing: ".04em" }}>
                        {s.pecet}
                      </div>
                      {s.poznamka && (
                        <div style={{ fontSize: 12.5, color: "var(--soft)", marginTop: 6 }}>{s.poznamka}</div>
                      )}
                    </div>
                  );
                })}

                {nepovedlo.length > 0 && (
                  <div className="stop-why warn" style={{ marginTop: 14, textAlign: "left" }}>
                    {nepovedlo.map((n) => (
                      <div key={n.pecet} style={{ marginBottom: 4 }}>
                        <b className="mono">{n.pecet}</b> — {n.duvod}
                      </div>
                    ))}
                  </div>
                )}

                {potvrdit.isError && (
                  <div style={{ marginTop: 8, fontSize: 12.5, color: "var(--bad)", textAlign: "center" }}>
                    Chyba: {String((potvrdit.error as Error)?.message)}
                  </div>
                )}
              </>
            )}
          </div></div>

          <div className="bottom">
            {sacky.length === 0 ? (
              <button className="cta" onClick={() => history.goBack()}>Zpět</button>
            ) : (
              <button
                className="cta"
                disabled={vzato.size === 0 || potvrdit.isPending}
                onClick={() => potvrdit.mutate()}
              >
                {potvrdit.isPending
                  ? "Odesílám…"
                  : vzato.size === 0
                    ? "Odškrtni, co máš v autě"
                    : `Dokončit naložení (${vzato.size})`}
              </button>
            )}
          </div>
        </div>
      </IonContent>
    </IonPage>
  );
}
